import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Badge, Button, Spinner, Row, Col, Alert, Modal, Form } from 'react-bootstrap';
import {
  DocumentTextIcon,
  ArrowLeftIcon,
  CalendarIcon,
  TruckIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/solid';
import { useNavigate, useParams } from 'react-router-dom';
import { orderAPI, recurringAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Order } from '../../types';
import Header from '../common/Header';
import Footer from '../common/Footer';

const OrderDetails: React.FC = () => {
  const navigate = useNavigate();
  const { user, isExpired } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState('');
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringForm, setRecurringForm] = useState({ frequency: 'weekly', next_delivery_date: '', endType: 'never' as 'never' | 'date', end_date: '' });
  const [recurringLoading, setRecurringLoading] = useState(false);
  const [recurringSuccess, setRecurringSuccess] = useState('');

  const leadDays = (user as any)?.min_delivery_lead_days ?? 0;
  const minDeliveryDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + leadDays);
    return d.toISOString().split('T')[0];
  })();

  const calcNextDeliveryDate = (deliveryDate: string | undefined, frequency: string) => {
    const base = deliveryDate ? new Date(deliveryDate + 'T00:00:00') : new Date();
    if (frequency === 'weekly') base.setDate(base.getDate() + 7);
    else if (frequency === 'biweekly') base.setDate(base.getDate() + 14);
    else if (frequency === 'monthly') base.setMonth(base.getMonth() + 1);
    const min = new Date(minDeliveryDate + 'T00:00:00');
    const result = base >= min ? base : min;
    return result.toISOString().split('T')[0];
  };

  useEffect(() => {
    if (id) {
      fetchOrder(parseInt(id));
    }
  }, [id]);

  const fetchOrder = async (orderId: number) => {
    try {
      const response = await orderAPI.getOrder(orderId);
      setOrder(response.data);
    } catch (err) {
      console.error('Error fetching order:', err);
      setError('Failed to load order details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'delivered': return 'success';
      case 'processing': return 'warning';
      case 'pending': return 'secondary';
      case 'confirmed': return 'info';
      case 'shipped': return 'primary';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `$${num.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="d-flex flex-column min-vh-100 app-background">
        <Header />
        <div className="flex-grow-1 d-flex justify-content-center align-items-center">
          <Spinner animation="border" variant="primary" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="d-flex flex-column min-vh-100 app-background">
        <Header />
        <div className="flex-grow-1">
          <Container className="py-5 text-center">
            <h3 className="text-danger mb-3">Error</h3>
            <p className="text-muted">{error || 'Order not found.'}</p>
            <Button variant="primary" onClick={() => navigate('/orders')}>
              <ArrowLeftIcon style={{ width: '16px', height: '16px' }} className="me-2" />
              Back to Orders
            </Button>
          </Container>
        </div>
        <Footer />
      </div>
    );
  }

  const totalAmount = typeof order.total_amount === 'string'
    ? parseFloat(order.total_amount)
    : order.total_amount;

  return (
    <div className="d-flex flex-column min-vh-100 app-background">
      <Header />
      <div className="flex-grow-1">
        <Container className="py-3 py-md-5 page-content-padding">
          {/* Back Button */}
          <Button
            variant="outline-secondary"
            className="mb-4"
            onClick={() => navigate('/orders')}
          >
            <ArrowLeftIcon style={{ width: '16px', height: '16px' }} className="me-2" />
            Back to Orders
          </Button>

          {/* Page Header */}
          <div className="text-center mb-3 mb-md-5">
            <h1 className="display-5 fw-bold text-primary mb-2 d-flex align-items-center justify-content-center gap-2">
              <DocumentTextIcon className="page-header-icon" style={{ width: '36px', height: '36px' }} />
              Order {order.order_number}
            </h1>
            <Badge
              bg={getStatusVariant(order.status)}
              className="fs-6 px-3 py-2 rounded-pill"
            >
              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
            </Badge>
          </div>

          {/* Order Info Cards */}
          <Row className="mb-4 g-3">
            <Col xs={6} md={6} lg={3}>
              <Card className="professional-card h-100 text-center">
                <Card.Body className="p-2 p-sm-3">
                  <CalendarIcon style={{ width: '28px', height: '28px' }} className="text-primary mb-2" />
                  <h6 className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>Order Date</h6>
                  <p className="fw-bold mb-0" style={{ fontSize: 'clamp(0.8rem, 2.5vw, 1rem)' }}>
                    {(() => { const d = order.created_at || order.order_date; if (!d) return '—'; if (d.includes('T')) return new Date(d).toLocaleDateString(); const [y,m,dd] = d.split('-').map(Number); return `${m}/${dd}/${y}`; })()}
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={6} lg={3}>
              <Card className="professional-card h-100 text-center">
                <Card.Body className="p-2 p-sm-3">
                  <TruckIcon style={{ width: '28px', height: '28px' }} className="text-info mb-2" />
                  <h6 className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>Delivery Date</h6>
                  <p className="fw-bold mb-0" style={{ fontSize: 'clamp(0.8rem, 2.5vw, 1rem)' }}>
                    {order.delivery_date
                      ? (() => { const [y,m,d] = order.delivery_date.split('-').map(Number); return `${m}/${d}/${y}`; })()
                      : '—'}
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={6} lg={3}>
              <Card className="professional-card h-100 text-center">
                <Card.Body className="p-2 p-sm-3">
                  <ClipboardDocumentListIcon style={{ width: '28px', height: '28px' }} className="text-warning mb-2" />
                  <h6 className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>Items</h6>
                  <p className="fw-bold mb-0" style={{ fontSize: 'clamp(0.8rem, 2.5vw, 1rem)' }}>
                    {order.items ? order.items.length : 0} product{order.items && order.items.length !== 1 ? 's' : ''}
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={6} lg={3}>
              <Card className="professional-card h-100 text-center">
                <Card.Body className="p-2 p-sm-3">
                  <CurrencyDollarIcon style={{ width: '28px', height: '28px' }} className="text-success mb-2" />
                  <h6 className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>Total Amount</h6>
                  <p className="fw-bold mb-0 text-success" style={{ fontSize: 'clamp(0.9rem, 3vw, 1.25rem)' }}>
                    {formatCurrency(totalAmount)}
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Order Items Table */}
          <Card className="professional-card mb-4">
            <Card.Header>
              <h5 className="mb-0 d-flex align-items-center gap-2">
                <DocumentTextIcon style={{ width: '20px', height: '20px' }} />
                Order Items
              </h5>
            </Card.Header>
            <Card.Body>
              {order.items && order.items.length > 0 ? (
                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Product</th>
                      <th>SKU</th>
                      <th className="text-center">Quantity</th>
                      <th className="text-end">Unit Price</th>
                      <th className="text-end">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((item, index) => (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        <td><strong>{item.product.name}</strong></td>
                        <td className="text-muted">{item.product.sku}</td>
                        <td className="text-center">
                          {item.quantity} {item.product.unit}{item.quantity > 1 ? 's' : ''}
                        </td>
                        <td className="text-end">{formatCurrency(item.unit_price)}</td>
                        <td className="text-end fw-bold">{formatCurrency(item.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5} className="text-end fw-bold fs-5">Total:</td>
                      <td className="text-end fw-bold fs-5 text-success">
                        {formatCurrency(totalAmount)}
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              ) : (
                <p className="text-muted text-center py-3 mb-0">No items in this order.</p>
              )}
            </Card.Body>
          </Card>

          {/* Notes */}
          {order.notes && (
            <Card className="professional-card mb-4">
              <Card.Header>
                <h5 className="mb-0">Notes</h5>
              </Card.Header>
              <Card.Body>
                <p className="mb-0">{order.notes}</p>
              </Card.Body>
            </Card>
          )}

          {/* Actions */}
          {duplicateError && (
            <Alert variant="danger" dismissible onClose={() => setDuplicateError('')} className="mb-3">
              {duplicateError}
            </Alert>
          )}
          <div className="d-flex gap-3 flex-wrap">
            <Button variant="outline-secondary" onClick={() => navigate('/orders')}>
              <ArrowLeftIcon style={{ width: '16px', height: '16px' }} className="me-2" />
              Back to Orders
            </Button>
            {order.status === 'pending' && (
              <Button variant="outline-warning" onClick={() => navigate(`/orders/${order.id}/edit`)} disabled={isExpired}>
                <PencilSquareIcon style={{ width: '16px', height: '16px' }} className="me-2" />
                Edit Order
              </Button>
            )}
            <Button
              variant="outline-info"
              disabled={duplicating || isExpired}
              onClick={async () => {
                setDuplicating(true);
                try {
                  const res = await orderAPI.duplicateOrder(order.id);
                  navigate(`/orders/${res.data.id}`);
                } catch {
                  setDuplicateError('Failed to duplicate order.');
                } finally {
                  setDuplicating(false);
                }
              }}
            >
              {duplicating ? (
                <><Spinner size="sm" className="me-2" />Duplicating...</>
              ) : (
                <><DocumentDuplicateIcon style={{ width: '16px', height: '16px' }} className="me-2" />Reorder</>
              )}
            </Button>
            <Button variant="primary" onClick={() => navigate('/products')} disabled={isExpired}>
              Place New Order
            </Button>
            <Button
              variant="outline-success"
              disabled={isExpired}
              onClick={() => {
                const defaultDate = calcNextDeliveryDate(order?.delivery_date, 'weekly');
                setRecurringForm({ frequency: 'weekly', next_delivery_date: defaultDate, endType: 'never', end_date: '' });
                setShowRecurringModal(true);
              }}
            >
              <ArrowPathIcon style={{ width: '16px', height: '16px' }} className="me-2" />
              Make Recurring
            </Button>
          </div>

          {recurringSuccess && (
            <Alert variant="success" className="mt-3" dismissible onClose={() => setRecurringSuccess('')}>
              {recurringSuccess}
            </Alert>
          )}
        </Container>
      </div>
      <Footer />

      {/* Make Recurring Modal */}
      <Modal show={showRecurringModal} onHide={() => setShowRecurringModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Set Up Recurring Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            This order's items will be automatically reordered on the schedule you choose.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Frequency</Form.Label>
            <Form.Select
              value={recurringForm.frequency}
              onChange={e => {
                const freq = e.target.value;
                const newDate = calcNextDeliveryDate(order?.delivery_date, freq);
                setRecurringForm(prev => ({ ...prev, frequency: freq, next_delivery_date: newDate }));
              }}
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-Weekly</option>
              <option value="monthly">Monthly</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>First Delivery Date</Form.Label>
            <Form.Control
              type="date"
              min={minDeliveryDate}
              value={recurringForm.next_delivery_date}
              onChange={e => setRecurringForm(prev => ({ ...prev, next_delivery_date: e.target.value }))}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Duration</Form.Label>
            <div>
              <Form.Check
                type="radio"
                id="od-endType-never"
                label="Until I cancel"
                checked={recurringForm.endType === 'never'}
                onChange={() => setRecurringForm(prev => ({ ...prev, endType: 'never', end_date: '' }))}
              />
              <Form.Check
                type="radio"
                id="od-endType-date"
                label="Until a specific date"
                checked={recurringForm.endType === 'date'}
                onChange={() => setRecurringForm(prev => ({ ...prev, endType: 'date' }))}
              />
            </div>
            {recurringForm.endType === 'date' && (
              <Form.Control
                type="date"
                className="mt-2"
                min={recurringForm.next_delivery_date || minDeliveryDate}
                value={recurringForm.end_date}
                onChange={e => setRecurringForm(prev => ({ ...prev, end_date: e.target.value }))}
              />
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRecurringModal(false)}>Cancel</Button>
          <Button
            variant="success"
            disabled={recurringLoading || !recurringForm.next_delivery_date || (recurringForm.endType === 'date' && !recurringForm.end_date)}
            onClick={async () => {
              setRecurringLoading(true);
              try {
                await recurringAPI.createRecurringOrder({
                  source_order_id: order!.id,
                  frequency: recurringForm.frequency,
                  next_delivery_date: recurringForm.next_delivery_date,
                  end_date: recurringForm.endType === 'date' ? recurringForm.end_date : null,
                });
                setShowRecurringModal(false);
                setRecurringSuccess('Recurring order created! Manage it from the Recurring Orders page.');
              } catch {
                setError('Failed to create recurring order.');
                setShowRecurringModal(false);
              } finally {
                setRecurringLoading(false);
              }
            }}
          >
            {recurringLoading ? <><Spinner size="sm" className="me-2" />Creating...</> : 'Create Recurring Order'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrderDetails;
